import React from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const DocumentFilters = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  documentCount,
  transactions 
}) => {
  const transactionOptions = [
    { value: '', label: 'All Transactions' },
    ...transactions?.map(transaction => ({
      value: transaction?.id,
      label: `#${transaction?.id} - ${transaction?.propertyAddress}`
    }))
  ];

  const documentTypeOptions = [
    { value: '', label: 'All Document Types' },
    { value: 'deed', label: 'Property Deed' },
    { value: 'contract', label: 'Purchase Contract' },
    { value: 'assessment', label: 'Property Assessment' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'report', label: 'Inspection Report' },
    { value: 'other', label: 'Other Documents' }
  ];

  const signatureStatusOptions = [
    { value: '', label: 'All Signature Status' },
    { value: 'pending', label: 'Pending Signature' },
    { value: 'signed', label: 'Signed' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'expired', label: 'Expired' }
  ];

  const blockchainStatusOptions = [
    { value: '', label: 'All Blockchain Status' },
    { value: 'verified', label: 'Blockchain Verified' },
    { value: 'pending', label: 'Verification Pending' }
  ];

  const hasActiveFilters = Object.values(filters)?.some(value => value !== '');

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft p-6 space-y-6">
      {/* Search Bar */}
      <div className="relative">
        <Input
          type="search"
          placeholder="Search documents by filename, content, or transaction..."
          value={filters?.search}
          onChange={(e) => onFilterChange('search', e?.target?.value)}
          className="pl-10"
        />
        <Icon 
          name="Search" 
          size={16} 
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
        />
      </div>
      {/* Filter Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Select
          label="Transaction"
          options={transactionOptions}
          value={filters?.transaction}
          onChange={(value) => onFilterChange('transaction', value)}
          searchable
        />

        <Select
          label="Document Type"
          options={documentTypeOptions}
          value={filters?.documentType}
          onChange={(value) => onFilterChange('documentType', value)}
        />

        <Select
          label="Signature Status"
          options={signatureStatusOptions}
          value={filters?.signatureStatus}
          onChange={(value) => onFilterChange('signatureStatus', value)}
        />

        <Select
          label="Blockchain Status"
          options={blockchainStatusOptions}
          value={filters?.blockchainStatus}
          onChange={(value) => onFilterChange('blockchainStatus', value)}
        />
      </div>
      {/* Date Range Filters */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="date"
          label="Upload Date From"
          value={filters?.dateFrom}
          onChange={(e) => onFilterChange('dateFrom', e?.target?.value)}
        />

        <Input
          type="date"
          label="Upload Date To"
          value={filters?.dateTo}
          onChange={(e) => onFilterChange('dateTo', e?.target?.value)}
        />
      </div>
      {/* Filter Actions and Results */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{documentCount}</span> documents
          </div>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
              className="text-muted-foreground hover:text-foreground"
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Quick Filter Badges */}
        <div className="hidden lg:flex items-center space-x-2">
          {filters?.signatureStatus === 'pending' && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-warning/10 text-warning rounded-full text-xs font-medium">
              <Icon name="Clock" size={12} />
              <span>Pending Signatures</span>
            </span>
          )}
          {filters?.blockchainStatus === 'verified' && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
              <Icon name="Shield" size={12} />
              <span>Blockchain Verified</span>
            </span>
          )}
          {filters?.documentType && (
            <span className="inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
              <Icon name="FileText" size={12} />
              <span className="capitalize">{filters?.documentType}</span>
            </span>
          )}
        </div>
      </div>
      {/* Mobile Filter Summary */}
      <div className="lg:hidden">
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(filters)?.map(([key, value]) => {
              if (!value || key === 'search') return null;
              
              let displayValue = value;
              if (key === 'transaction') {
                const transaction = transactions?.find(t => t?.id === value);
                displayValue = transaction ? `#${transaction?.id}` : value;
              }
              
              return (
                <span 
                  key={key}
                  className="inline-flex items-center space-x-1 px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs"
                >
                  <span className="capitalize">{key}: {displayValue}</span>
                  <button
                    onClick={() => onFilterChange(key, '')}
                    className="hover:text-foreground transition-smooth"
                  >
                    <Icon name="X" size={10} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentFilters;